import { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { View, Text, Card, Button, Icon, ProgressBar, Badge } from "../base";
import { IconButton } from "../base/IconButton";
import { useAuth } from "../../hooks/useAuth";
import { useAppDispatch, useAppSelector } from "../../redux";
import { agentActions } from "../../redux/reducers/agentSlice";
import { useOnboardingState } from "./useOnboardingState";
import { ONBOARDING_DRAWER_CLOSE_EVENT } from "./onboardingEvents";
import { ONBOARDING_STEPS, ONBOARDING_PHASE_2_STEPS, PANEL_SUBTITLE, PHASE_1_LABEL, PHASE_2_LABEL, type OnboardingStepDef } from "./onboardingSteps";

const CopyButton = ({ text }: { text: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <IconButton
      variant="info"
      icon={copied ? "check" : "copy"}
      onClick={handleCopy}
      title="Copy to clipboard"
      className={copied ? "[&_svg]:!text-emerald-500" : ""}
    />
  );
};

/** Resolved dynamic route map passed from parent to each StepRow. */
type DynamicRouteMap = Record<string, string>;

type StepRowProps = {
  stepDef: OnboardingStepDef;
  stepIndex: number;
  isCompleted: boolean;
  isCurrent: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onManualComplete: () => void;
  dynamicRoutes: DynamicRouteMap;
};

const StepRow = ({ stepDef, stepIndex, isCompleted, isCurrent, isExpanded, onToggle, onManualComplete, dynamicRoutes }: StepRowProps) => {
  const navigate = useNavigate();

  return (
    <View className="flex flex-col">
      {/* Step header row */}
      <button
        className="flex flex-row items-center gap-2 py-1 px-1 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left w-full group"
        onClick={onToggle}
      >
        {/* Step indicator */}
        {isCompleted ? (
          <Badge
            variant="success"
            className="!px-0 !py-0 !w-5 !h-5 !min-w-5 flex items-center justify-center !rounded-full shrink-0"
          >
            <Icon
              name="check"
              size={12}
            />
          </Badge>
        ) : (
          <Badge
            variant={isCurrent ? "info" : "default"}
            className="!px-0 !py-0 !w-5 !h-5 !min-w-5 flex items-center justify-center !rounded-full shrink-0 !text-[10px] !font-semibold"
          >
            {stepIndex + 1}
          </Badge>
        )}

        {/* Step title */}
        <Text
          className={`text-sm flex-1 transition-colors ${
            isCompleted
              ? "text-gray-400 dark:text-gray-500 line-through"
              : isCurrent
                ? "font-medium text-gray-900 dark:text-white"
                : "text-gray-500 dark:text-gray-400"
          }`}
        >
          {stepDef.title}
        </Text>

        {/* Expand/collapse chevron */}
        <Icon
          name={isExpanded ? "chevron-down" : "chevron-right"}
          size={14}
          className="text-gray-300 dark:text-gray-600 group-hover:text-gray-400 dark:group-hover:text-gray-500 transition-colors shrink-0"
        />
      </button>

      {/* Expanded content */}
      {isExpanded && (
        <View className="pl-7 pr-1 pb-2 flex flex-col gap-2">
          {/* Description */}
          <Text className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{stepDef.description}</Text>

          {/* CLI commands */}
          {stepDef.commands.map((cmd, i) => (
            <View
              key={i}
              className="flex flex-col gap-0.5"
            >
              <View className="flex flex-row items-center gap-1 bg-gray-900 dark:bg-gray-950 rounded-md px-2.5 py-1.5">
                <Text className="text-xs font-mono text-gray-100 flex-1 select-all">{cmd.command}</Text>
                <CopyButton text={cmd.command} />
              </View>
              <Text className="text-[11px] text-gray-400 dark:text-gray-500 leading-snug">{cmd.description}</Text>
            </View>
          ))}

          {/* Action buttons */}
          {stepDef.actions.map((action, i) => {
            const resolvedHref = action.dynamicRoute ? dynamicRoutes[action.dynamicRoute] : action.href;

            return (
              <Button
                key={i}
                variant="info"
                size="auto"
                className="px-4 py-2"
                onClick={() => {
                  if (action.isRoute && resolvedHref) {
                    // Close the drawer before navigating so it doesn't cover the destination page
                    window.dispatchEvent(new CustomEvent(ONBOARDING_DRAWER_CLOSE_EVENT));
                    navigate(resolvedHref);
                  } else if (resolvedHref) {
                    window.open(resolvedHref, "_blank", "noopener,noreferrer");
                  }
                }}
              >
                {action.label}
                {!action.isRoute && (
                  <Icon
                    name="open"
                    size={12}
                  />
                )}
              </Button>
            );
          })}

          {/* Manual completion button */}
          {!isCompleted && (
            <Button
              variant="success"
              size="auto"
              className="px-4 py-2"
              onClick={(e) => {
                e.stopPropagation();
                onManualComplete();
              }}
            >
              <Icon
                name="check"
                size={14}
              />
              {stepDef.manualButtonLabel}
            </Button>
          )}
        </View>
      )}
    </View>
  );
};

/** Resolve dynamic route identifiers to concrete paths. */
const useDynamicRoutes = (): DynamicRouteMap => {
  const dispatch = useAppDispatch();
  const agents = useAppSelector((state) => state.agent.entities);
  const agentIds = useAppSelector((state) => state.agent.ids);
  const isLoading = useAppSelector((state) => state.agent.isLoading);
  const releaseEntities = useAppSelector((state) => state.release.entities);

  // Ensure agents are loaded — the drawer can open from any page.
  useEffect(() => {
    if (agentIds.length === 0 && !isLoading) {
      dispatch(agentActions.get({}));
    }
  }, [agentIds.length, isLoading, dispatch]);

  return useMemo(() => {
    // Pick the most recently created agent — the onboarding flow is about
    // a NEW agent, so createdAt is the right signal. Using lastActivityAt
    // would let an old agent with live traffic overshadow the one they
    // just scaffolded.
    const sorted = agentIds
      .map((id) => agents[id])
      .filter(Boolean)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    const agentId = sorted[0]?.id;

    // v1 agents use /deployments, v2 agents use /releases. Best-effort
    // check: if we have release data for this agent, link to releases;
    // otherwise default to deployments (safe for both — the route always
    // exists, and AgentDetailLayout will show the correct tab on arrival).
    const hasReleases = agentId && Object.values(releaseEntities).some((r) => r.agentId === agentId);
    const deploymentsPath = hasReleases ? "releases" : "deployments";

    return {
      "agent-overview": agentId ? `/agents/${agentId}/overview` : "/agents",
      "agent-function-debugger": agentId ? `/agents/${agentId}/function?debugger=open` : "/agents",
      "agent-sessions-trace": agentId ? `/agents/${agentId}/sessions?tab=trace` : "/agents",
      "agent-eval-assignments": agentId ? `/agents/${agentId}/evaluation/assignments` : "/agents",
      "agent-releases": agentId ? `/agents/${agentId}/${deploymentsPath}` : "/agents",
    };
  }, [agents, agentIds, releaseEntities]);
};

export const OnboardingPanel = () => {
  const { user } = useAuth();
  const userId = user?.id;
  const { steps, completeStep, resetSteps, completedCount, totalSteps, allCompleted } = useOnboardingState(userId);
  const dynamicRoutes = useDynamicRoutes();

  // Track which step is expanded. Default: first incomplete step.
  const firstIncompleteIndex = steps.findIndex((s) => !s.completed);
  const [expandedStepId, setExpandedStepId] = useState<string | null>(null);

  // If user hasn't manually expanded anything, auto-expand the current step
  const effectiveExpandedId = expandedStepId ?? (firstIncompleteIndex >= 0 ? steps[firstIncompleteIndex].id : null);

  const handleToggleStep = useCallback((stepId: string) => {
    setExpandedStepId((prev) => (prev === stepId ? null : stepId));
  }, []);

  const progressPercent = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

  return (
    <Card className="p-3 overflow-hidden">
      {/* Subtitle */}
      <Text className="text-xs text-gray-400 dark:text-gray-500">{PANEL_SUBTITLE}</Text>

      {/* Progress bar */}
      <ProgressBar
        progress={progressPercent}
        variant="brand"
        className="mt-4 mb-4 !h-1.5"
      />

      {allCompleted ? (
        <View className="flex flex-col items-center gap-2 py-3">
          <Icon
            name="check"
            size={20}
            className="text-emerald-500"
          />
          <Text className="text-[13px] font-medium text-gray-900 dark:text-white">You're set.</Text>
          <Text className="text-[11px] text-gray-500 dark:text-gray-400 text-center">Close this panel — or revisit any step from the docs.</Text>
          <Button
            variant="neutral"
            size="auto"
            className="px-4 py-2"
            onClick={resetSteps}
          >
            Start over
          </Button>
        </View>
      ) : (
        <>
          {/* Phase 1 label */}
          <Text className="text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5 px-1">{PHASE_1_LABEL}</Text>

          {/* Phase 1 Steps */}
          <View className="flex flex-col">
            {ONBOARDING_STEPS.map((stepDef, i) => {
              const stepState = steps[i];
              const isCurrent = firstIncompleteIndex === i;
              const isExpanded = effectiveExpandedId === stepDef.id;

              return (
                <StepRow
                  key={stepDef.id}
                  stepDef={stepDef}
                  stepIndex={i}
                  isCompleted={stepState.completed}
                  isCurrent={isCurrent}
                  isExpanded={isExpanded}
                  onToggle={() => handleToggleStep(stepDef.id)}
                  onManualComplete={() => completeStep(stepDef.id)}
                  dynamicRoutes={dynamicRoutes}
                />
              );
            })}
          </View>

          {/* Phase 2 label */}
          <Text className="text-[11px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-0.5 mt-2 px-1">{PHASE_2_LABEL}</Text>

          {/* Phase 2 Steps */}
          <View className="flex flex-col">
            {ONBOARDING_PHASE_2_STEPS.map((stepDef, i) => {
              const globalIndex = ONBOARDING_STEPS.length + i;
              const stepState = steps[globalIndex];
              const isCurrent = firstIncompleteIndex === globalIndex;
              const isExpanded = effectiveExpandedId === stepDef.id;

              return (
                <StepRow
                  key={stepDef.id}
                  stepDef={stepDef}
                  stepIndex={globalIndex}
                  isCompleted={stepState.completed}
                  isCurrent={isCurrent}
                  isExpanded={isExpanded}
                  onToggle={() => handleToggleStep(stepDef.id)}
                  onManualComplete={() => completeStep(stepDef.id)}
                  dynamicRoutes={dynamicRoutes}
                />
              );
            })}
          </View>
        </>
      )}
    </Card>
  );
};
